/**
 * **回合队列** —— 左侧那一列"谁在打、谁是下一个"。
 *
 * 评审：
 *
 * > 玩家回合队列显示在左侧，每一行显示一个人的名字和手牌数，正在进行回合的玩家
 * > 不透明度和尺寸最大，之前和之后的玩家按顺序左对齐显示在其上和其下，
 * > 且透明度和尺寸依次降低。
 *
 * ## 站位：固定偏移槽位（这一版改的就是它）
 *
 * 评审：
 *
 * > 回合队列的实现方式不对，固定显示偏移 0（当前玩家），偏移 -1，-2，-3，
 * > +1，+2，+3 以此类推。轮到下一个玩家时，将所有文本的样式和位置与下一个
 * > 进行补间动画。
 *
 * 所以每个人的位置**不再是"我排在第几个"**，而是**"我离行动中那个人有多远"**：
 *
 * ```
 *   offset -2  PLAYER-4   8 张     ← 上上轮打过的
 *   offset -1  PLAYER-3   8 张     ← 上一轮打过的
 *   offset  0 ▶PLAYER-1   8 张     ← 行动中（这一行永远在 ACTIVE_ROW）
 *   offset +1  PLAYER-2   8 张     ← 下一个
 *   offset +2  PLAYER-5   8 张     ← 再下一个
 * ```
 *
 * 偏移怎么算（含"正对面那一个算下面"的平局规则）在 `turnSlots.ts`，
 * 那里是**纯算术**、在 node 里逐条钉着。
 *
 * ## 换人 = 一次补间，不是一次滚动
 *
 * 上一版是"整列往上滚一行 + 顺序轮转一格"。它的动作是"一列一起往上走"——
 * 每个人的落点都是"上一个人刚才待的地方"，滚动本身不表达"谁轮到谁"。
 *
 * 现在换人时每个人的偏移都会变（一般全列各挪一格），于是
 * **所有人的位置和样式一起补间到新槽位**，`TURN_MS` 一次走完。
 *
 * ## ⚠️ 样式也得补间，所以行里的文字**不能重建**
 *
 * "尺寸依次降低、透明度依次降低"以前是**每次重画整行**实现的（按新的
 * `distance` 重造 `Text`）。那样只能"啪"地换一档，没有补间可言。
 *
 * 所以每一行的文字在**建行的时候造一次**，之后只改内容：
 *
 * | 要补间的东西 | 怎么补 |
 * | --- | --- |
 * | 位置 | `view.y` |
 * | 尺寸 | `view.scale`（**字号不动** —— 动字号要重栅格化字形） |
 * | 不透明度 | 名字 / 张数 / 记号各自的 `alpha` |
 * | 字重 | 同样的文字造**两层**（常规 + 粗体）完全重叠，交叉淡入淡出 |
 * | 颜色 | 文字画成白色、颜色走 `tint`（`tint` 是免费的，改 `fill` 要重画纹理） |
 *
 * 最后两条是这一版的关键：`fontWeight` 和 `fill` 都**没法插值**，
 * 一个要重新栅格化、一个要重画纹理；而"交叉淡入淡出的两层字"和 `tint`
 * 都能每帧改，这才是"样式补间"能成立的前提。
 */

import { Container, Rectangle } from 'pixi.js';
import type { Text } from 'pixi.js';
import { richLabel } from '../ui/richText';
import { INK, TRACKING } from '../ui/ink';
import { mixHex } from '../palette';
import { easeOut } from '../ui/motion';
import { uiPad } from '../ui/style';
import { getLang, t, type Lang } from '../../i18n';
import { displayOffsets, slotStyle, type SlotStyle } from './turnSlots';
import { canHoverNow } from '../input/current';
import type { PlayerViewPlayer } from '../../core/project';
import type { PlayerId } from '../../core/ids';

/** 行动中的人显示在第几行（上面留几行给"之前的人"） */
const ACTIVE_ROW = 2;
/** 一行多高（设计像素，乘 UI 倍率） */
const ROW_HEIGHT = 44;
/** 一次换位的补间多久走完（毫秒）。太快看不见、太慢像卡住 */
const TURN_MS = 260;
/** 名字的基准字号。⚠️ **基准**：实际大小靠 `view.scale` 缩，见文件头 */
const NAME_SIZE = 17;
/** 张数的基准字号 */
const COUNT_SIZE = 13;
/** 张数离名字多远（设计像素） */
const COUNT_GAP = 10;
/** 张数最靠左到哪（名字很短时也不至于贴在一起） */
const COUNT_MIN_X = 96;
/** "点它"那个记号离左边多远 */
const MARK_X = 150;

export interface TurnQueueOptions {
  /** UI 倍率 */
  readonly scale: number;
  /** 队列可用宽度（左对齐用） */
  readonly width: number;
  /** 点了某个人（`setTargets` 里点得动的那几个才会回调） */
  readonly onPickTarget?: (playerId: PlayerId) => void;
  /**
   * **指针停在哪个可点的名字上**（`null` = 移开了）。
   *
   * 目标高亮的屏幕边缘光要用它：行动者把鼠标扫过谁的名字，谁那边就亮起来。
   */
  readonly onHoverTarget?: (playerId: PlayerId | null) => void;
}

/** 一行**现在的**样子。补间的两端都从它派生 */
interface RowLook extends SlotStyle {
  /** 这一行在容器里的 y */
  readonly y: number;
}

interface Row {
  readonly id: PlayerId;
  readonly view: Container;
  /** 常规字重与粗体两层，完全重叠 —— 交叉淡入淡出就是"字重补间" */
  readonly name: Text;
  readonly nameBold: Text;
  readonly count: Text;
  readonly countBold: Text;
  /** "◂ 点它"。一直在，靠 alpha 进出（所以它也能补间） */
  readonly mark: Text;

  name0: string;
  handCount: number;
  /** 造这一行时用的语言。语言热切换要据此重算文字 */
  lang: Lang;
  alive: boolean;
  target: boolean;

  /** 现在停在哪儿。补间的**起点**从这里取（半路再来一次不会跳） */
  look: RowLook;
  /** 这一次补间的起点 / 终点。`null` = 没在补间 */
  from: RowLook | null;
  to: RowLook | null;
}

export class TurnQueue {
  readonly view: Container;

  readonly #scale: number;
  readonly #rows: Row[] = [];
  /** 座次顺序。**不轮转** —— 站位是偏移算出来的，见文件头 */
  #seats: readonly PlayerViewPlayer[] = [];
  #activeId: PlayerId | null = null;
  #targetSet: ReadonlySet<PlayerId> = new Set();
  /** 指针现在停在哪一行的 id（`null` = 没有）。用来在可点集合变化时清掉悬停 */
  #hoverId: PlayerId | null = null;
  #elapsed = 0;
  #tweening = false;

  constructor(options: TurnQueueOptions) {
    this.#scale = options.scale;
    // ⚠️ `width` 不再参与摆放：整列**左对齐在 x = 0**（评审要求），
    //    行里的东西都从这一条竖线往右排。留着这个参数是为了不打破调用方签名
    void options.width;

    this.view = new Container();
    this.view.label = 'turn-queue';
    this.onPickTarget = options.onPickTarget;
    this.onHoverTarget = options.onHoverTarget;
  }

  /** 点了某个人。调用方在 `update` 前设一次 */
  onPickTarget: ((playerId: PlayerId) => void) | undefined;
  /** 指针停在哪个可点的名字上（`null` = 移开）。同样在 `update` 前设 */
  onHoverTarget: ((playerId: PlayerId | null) => void) | undefined;

  /**
   * **哪几个玩家现在是可以点的对象。**
   *
   * 评审：「玩家想做什么直接对手牌进行操作就行了」—— 需要指定对象的那种牌
   * （单张 needsTarget、一对抢人）拖出去之后，**点队列里的名字**就是"对谁"。
   * 所以那个记号不是装饰，它是流程的下一步。
   */
  setTargets(ids: readonly PlayerId[]): void {
    const set = new Set(ids);
    let changed = false;
    for (const id of set) if (!this.#targetSet.has(id)) changed = true;
    for (const id of this.#targetSet) if (!set.has(id)) changed = true;
    if (!changed) return;

    this.#targetSet = set;
    // ★ 指针停着的那一行**可能刚好被取消可点**（比如行动结束、目标集合清空）：
    //   `#syncPickable` 会把它的 eventMode 关掉，之后 `pointerout` 收不到 ——
    //   不在这里主动清，悬停就会永远卡住，目标高亮于是一直亮着（用户报的）。
    if (this.#hoverId !== null && !set.has(this.#hoverId)) {
      this.#hoverId = null;
      this.onHoverTarget?.(null);
    }
    for (const row of this.#rows) {
      row.target = set.has(row.id);
      // 命中区和光标是"能不能按"，**不补间**；记号那个小字才补间
      this.#syncPickable(row);
    }
    this.#retarget(true);
  }

  /** 一行多高（逻辑像素）。宿主排版时要用 */
  get rowHeight(): number {
    return uiPad(ROW_HEIGHT, this.#scale);
  }

  /** 现在显示几个人 */
  get count(): number {
    return this.#rows.length;
  }

  /**
   * 行动中那一行**上面留了几行**（像素）。
   *
   * ⚠️ 调用方必须把容器往下挪这么多：行动中的人固定在 `ACTIVE_ROW` 那一行，
   * offset 为负的那几行 y 是负的 —— 不挪的话"之前的人"整排都在画布外面。
   */
  get anchorOffset(): number {
    return ACTIVE_ROW * this.rowHeight;
  }

  /** 现在在补间（诊断与冒烟用） */
  get moving(): boolean {
    return this.#tweening;
  }

  /**
   * 某个人**目标**槽位的偏移（不是补间中的中间值）。
   *
   * 查不到就是 `null`。诊断与冒烟用 —— "谁站在第几行"是这一列唯一要看的东西。
   */
  offsetOf(id: PlayerId): number | null {
    const row = this.#rows.find((entry) => entry.id === id);
    if (row === undefined) return null;
    const look = row.to ?? row.look;
    return Math.round(look.y / this.rowHeight);
  }

  /**
   * 更新名单与"轮到谁"。
   *
   * ⚠️ `players` 是**座次顺序**（不轮转）：谁站在第几行由偏移算，
   * 和数组下标无关。
   */
  setPlayers(players: readonly PlayerViewPlayer[], activeId: PlayerId | null): void {
    const sameRoster =
      this.#seats.length === players.length && this.#seats.every((p, i) => p.id === players[i]?.id);

    this.#seats = [...players];
    if (!sameRoster) {
      // 名册变了（理论上不会：出局的人仍在名单里，只是 `alive: false`）——
      // 真的变了就直接落位，别让消失/新增的行从别处滑进来
      this.#syncRoster(players);
      this.#activeId = activeId;
      this.#retarget(false);
      this.#writeTexts(players);
      return;
    }

    this.#writeTexts(players);
    const turned = activeId !== this.#activeId;
    this.#activeId = activeId;
    // ⚠️ **每次**都重定目标，但只有换人才补间：手牌数变了不该让整列动一下
    this.#retarget(turned);
  }

  /** 每帧推进补间。挂到那唯一一个 ticker 上 */
  update(deltaMS: number): void {
    if (!this.#tweening) return;
    this.#elapsed += deltaMS;
    const done = this.#elapsed >= TURN_MS;
    const t = easeOut(done ? 1 : this.#elapsed / TURN_MS);

    for (const row of this.#rows) {
      const { from, to } = row;
      if (from === null || to === null) continue;
      this.#apply(row, lerpLook(from, to, t));
      if (done) {
        row.look = to;
        row.from = null;
        row.to = null;
      }
    }
    if (done) this.#tweening = false;
  }

  destroy(): void {
    this.view.destroy({ children: true });
    this.#rows.length = 0;
  }

  // ============================================================
  // 内部
  // ============================================================

  /**
   * 按"现在轮到谁 + 谁能点"重新算每一行的目标槽位。
   *
   * `animate` 为假时直接落位（第一次、名册变了）—— 那些场合没有"从哪儿来"。
   */
  #retarget(animate: boolean): void {
    // ⚠️ **正在补间的时候不许"直接落位"。** 视图每次变化都会走 `setPlayers`，
    //    而它默认不补间（手牌数变了不该让整列动一下）—— 少这一道闸的话，
    //    补间走到一半时来一次视图更新，整列就会**啪地跳**到终点
    if (!animate && this.#tweening) return;

    const activeIndex = this.#seats.findIndex((p) => p.id === this.#activeId);
    const offsets = displayOffsets(this.#seats.length, activeIndex);

    for (let seat = 0; seat < this.#rows.length; seat++) {
      const row = this.#rows[seat];
      const player = this.#seats[seat];
      if (row === undefined || player === undefined) continue;
      const offset = offsets[seat] ?? 0;
      const to: RowLook = {
        ...slotStyle(offset, player.alive, this.#targetSet.has(player.id)),
        y: offset * this.rowHeight,
      };

      if (!animate) {
        row.look = to;
        row.from = null;
        row.to = null;
        this.#apply(row, to);
        continue;
      }
      row.from = row.look;
      row.to = to;
    }

    this.#elapsed = 0;
    this.#tweening = animate && this.#rows.some((row) => row.from !== null && row.to !== null);
  }

  /** 把一行的样子写进 Pixi 节点。**唯一**写位置/样式的地方 */
  #apply(row: Row, look: RowLook): void {
    row.look = look;
    row.view.position.set(0, look.y);
    row.view.scale.set(look.scale);

    // 字重是**两层字的交叉淡入淡出**，不是开关
    row.name.alpha = look.alpha * (1 - look.weight);
    row.nameBold.alpha = look.alpha * look.weight;
    row.count.alpha = look.alpha * (1 - look.weight);
    row.countBold.alpha = look.alpha * look.weight;
    row.mark.alpha = look.alpha * look.mark;

    // 颜色走 tint：改 `fill` 要重画纹理，`tint` 每帧改都是免费的
    row.name.tint = look.tint;
    row.count.tint = look.tint;
    // ⚠️ 记号不跟着这一行的 tint 走：它是**唯一**那个"点我"，
    //    永远用酸色（出局的人不会成为目标，所以不会打架）
    row.mark.tint = INK.accent;
  }

  /** 名册变了：造新行、去旧行。新行**直接落在**目标槽位上 */
  #syncRoster(players: readonly PlayerViewPlayer[]): void {
    const wanted = new Set(players.map((player) => player.id));
    for (let i = this.#rows.length - 1; i >= 0; i--) {
      const row = this.#rows[i];
      if (row === undefined || wanted.has(row.id)) continue;
      row.view.destroy({ children: true });
      this.#rows.splice(i, 1);
    }
    for (const player of players) {
      if (this.#rows.some((row) => row.id === player.id)) continue;
      const row = this.#makeRow(player);
      this.#rows.push(row);
      this.view.addChild(row.view);
    }
    // 行的顺序跟着座次走（`#retarget` 按下标取偏移）
    this.#rows.sort(
      (a, b) =>
        players.findIndex((p) => p.id === a.id) - players.findIndex((p) => p.id === b.id),
    );
    this.view.removeChildren();
    for (const row of this.#rows) this.view.addChild(row.view);
  }

  /**
   * 造一行。⚠️ 文字在这里造**一次**，之后只改内容（见文件头）：
   * 位置、尺寸、透明度、字重、颜色**全是补间出来的**，不是重画出来的。
   */
  #makeRow(player: PlayerViewPlayer): Row {
    const s = this.#scale;
    const view = new Container();
    view.label = `turn-queue-row-${player.id}`;

    const name = this.#text(player.name, NAME_SIZE, 'mono', false);
    const nameBold = this.#text(player.name, NAME_SIZE, 'mono', true);
    view.addChild(name, nameBold);

    // 张数摆在名字右边。⚠️ 名字是**固定字号**，所以这个 x 是常数
    const countX = Math.max(name.width + uiPad(COUNT_GAP, s), uiPad(COUNT_MIN_X, s));
    const count = this.#text(
      t('render.turnQueue.handCount', { n: player.handCount }),
      COUNT_SIZE,
      'cjk',
      false,
    );
    const countBold = this.#text(
      t('render.turnQueue.handCount', { n: player.handCount }),
      COUNT_SIZE,
      'cjk',
      true,
    );
    count.position.set(countX, uiPad(2, s));
    countBold.position.set(countX, uiPad(2, s));
    view.addChild(count, countBold);

    const mark = this.#text(t('render.turnQueue.pointIt'), COUNT_SIZE - 2, 'cjk', false);
    mark.position.set(uiPad(MARK_X, s), uiPad(2, s));
    mark.tint = INK.accent;
    mark.alpha = 0;
    view.addChild(mark);

    const row: Row = {
      id: player.id,
      view,
      name,
      nameBold,
      count,
      countBold,
      mark,
      name0: player.name,
      handCount: player.handCount,
      lang: getLang(),
      alive: player.alive,
      target: this.#targetSet.has(player.id),
      look: { ...slotStyle(0, player.alive), y: 0 },
      from: null,
      to: null,
    };

    // 点得动的时候才吃事件。⚠️ 监听器只挂一次，靠 `target` 判该不该响应
    view.eventMode = 'none';
    view.cursor = 'default';
    view.on('pointertap', () => {
      if (row.target) this.onPickTarget?.(row.id);
    });
    // ★ 指针进 / 出这一行（只有它能点时）—— 目标高亮靠它。
    //   ⚠️ 只有真能 hover 的指针才算：触控点完指针位置会滞留，高亮会卡住。
    view.on('pointerover', () => {
      if (!row.target || !canHoverNow()) return;
      this.#hoverId = row.id;
      this.onHoverTarget?.(row.id);
    });
    view.on('pointerout', () => {
      if (this.#hoverId !== row.id) return;
      this.#hoverId = null;
      this.onHoverTarget?.(null);
    });
    this.#syncPickable(row);
    return row;
  }

  /** 一行现在能不能点（决定命中区与光标）。**不补间** —— 它决定的是能不能按 */
  #syncPickable(row: Row): void {
    const s = this.#scale;
    const on = row.target;
    row.view.eventMode = on ? 'static' : 'none';
    row.view.cursor = on ? 'pointer' : 'default';
    if (on) {
      row.view.hitArea = new Rectangle(
        -uiPad(6, s),
        -uiPad(2, s),
        uiPad(170, s),
        uiPad(24, s),
      );
    }
  }

  /** 名字 / 张数 / 生死变了就改文本。**不重建节点** */
  #writeTexts(players: readonly PlayerViewPlayer[]): void {
    // ⚠️ 按 **id** 找，不按名字 —— 热座里两个人的昵称可能一模一样
    //    （`uniqueNames()` 只在开局时补过座位号）
    const byId = new Map(players.map((player) => [player.id, player]));
    for (const row of this.#rows) {
      const player = byId.get(row.id);
      if (player === undefined) continue;
      if (player.name !== row.name0) {
        row.name0 = player.name;
        row.name.text = player.name;
        row.nameBold.text = player.name;
      }
      // ⚠️ 语言也可能变（热切换）：出局玩家的张数**不再变化**，只比 handCount
      //    的话它的文字会永远停在旧语言。所以把语言也纳入判据。
      const lang = getLang();
      if (player.handCount !== row.handCount || lang !== row.lang) {
        row.handCount = player.handCount;
        row.lang = lang;
        const text = t('render.turnQueue.handCount', { n: player.handCount });
        row.count.text = text;
        row.countBold.text = text;
        row.mark.text = t('render.turnQueue.pointIt');
      }
      if (player.alive !== row.alive) row.alive = player.alive;
    }
  }

  /** 造一层文字。**白色 + tint** —— 这样颜色才是可补间的（见文件头） */
  #text(text: string, size: number, role: 'mono' | 'cjk', bold: boolean): Text {
    return richLabel({
      text,
      scale: this.#scale,
      size,
      fill: 0xffffff,
      role,
      bold,
      tracking: role === 'mono' ? TRACKING.normal : undefined,
      // ⚠️ 左对齐：几行名字的第一个字母要落在同一条竖线上
      align: 'left',
    });
  }
}

/** 两个样子之间插值。颜色走 `mixHex`（线性 RGB），其余直接线插 */
function lerpLook(from: RowLook, to: RowLook, t: number): RowLook {
  if (t <= 0) return from;
  if (t >= 1) return to;
  return {
    y: from.y + (to.y - from.y) * t,
    scale: from.scale + (to.scale - from.scale) * t,
    alpha: from.alpha + (to.alpha - from.alpha) * t,
    weight: from.weight + (to.weight - from.weight) * t,
    tint: mixHex(from.tint, to.tint, t),
    mark: from.mark + (to.mark - from.mark) * t,
  };
}
